import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  Divider,
  Avatar,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  Fade,
  Grow,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Add,
  Search,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getAllInventoryItems,
  addInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getInventoryCategories,
  getAllWarehouses
} from '../services/inventoryService';
import {
  getAllCategories,
  getSubcategories
} from '../services/categoryService';
import { fileToBase64 } from '../services/uploadService';
import { uploadImage as uploadToCloudinary } from '../services/cloudinaryService';
import InventoryItemCard from '../components/inventory/InventoryItemCard';
import { InventoryItem as BaseInventoryItem, ExtendedInventoryItem, Warehouse } from '../utils/types';
import { Category as CategoryType, SubCategory as SubCategoryType } from '../types/category';
import { DEFAULT_CURRENCY_SYMBOL } from '../utils/constants';
import { useCurrency } from '../contexts/CurrencyContext';
import { postProductToFacebook } from '../services/facebookService';

// نوع واجهة بيانات المخزون المستخدمة محلياً
interface LocalInventoryItem {
  id?: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  categoryId?: string;
  subcategoryId?: string;
  price: number;
  costPrice: number;
  quantity: number;
  minQuantity: number;
  supplier?: string;
  imageUrl?: string;
  sku?: string;
  barcode?: string;
  warehouseId?: string;
  warehouseName?: string;
  purchasePrice?: number;
  sellingPrice?: number;
  imageFile?: File | null;
  imageBase64?: string;
  image?: string;
  showInStore?: boolean;
  createdAt?: any;
  updatedAt?: any;
  isLowStock?: boolean;
  location?: string;
  unitPrice?: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  delay?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  delay = 0
}) => {
  const theme = useTheme();

  return (
    <Grow in timeout={1000 + delay}>
      <Card
        sx={{
          height: '100%',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          borderRadius: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${alpha(color, 0.3)}`,
            borderColor: alpha(color, 0.4)
          },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
            zIndex: 0
          }}
        />
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: color,
                width: 56,
                height: 56,
                boxShadow: `0 4px 14px ${alpha(color, 0.4)}`
              }}
            >
              {icon}
            </Avatar>
            {trend && (
              <Chip
                icon={trend.isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${trend.isPositive ? '+' : ''}${trend.value}%`}
                size="small"
                color={trend.isPositive ? 'success' : 'error'}
                variant="outlined"
              />
            )}
          </Box>
          <Typography variant="h3" fontWeight="bold" color="text.primary" gutterBottom>
            {value}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );
};

const InventoryPage: React.FC = () => {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();

  const [items, setItems] = useState<LocalInventoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<LocalInventoryItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [mainCategories, setMainCategories] = useState<CategoryType[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategoryType[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('all');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState<LocalInventoryItem>({
    name: '',
    description: '',
    category: '',
    price: 0,
    costPrice: 0,
    quantity: 0,
    minQuantity: 0,
    supplier: '',
    imageUrl: '',
    sku: `SKU-${Date.now()}`,
    warehouseId: 'default',
    warehouseName: 'المستودع الافتراضي',
    purchasePrice: 0,
    sellingPrice: 0
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const inventoryItems = await getAllInventoryItems();
      const localItems: LocalInventoryItem[] = inventoryItems.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        category: item.category || '',
        subcategory: item.subcategory || '',
        categoryId: item.categoryId || '',
        subcategoryId: item.subcategoryId || '',
        price: item.sellingPrice || 0,
        costPrice: item.purchasePrice || 0,
        quantity: item.quantity || 0,
        minQuantity: item.minQuantity || 0,
        supplier: item.supplier || '',
        imageUrl: item.imageUrl || '',
        sku: item.sku || '',
        barcode: item.barcode || '',
        warehouseId: item.warehouseId || 'default',
        warehouseName: item.warehouseName || 'المستودع الافتراضي',
        purchasePrice: item.purchasePrice || 0,
        sellingPrice: item.sellingPrice || 0,
        unitPrice: item.unitPrice || 0,
        showInStore: item.showInStore || false,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        isLowStock: item.isLowStock || false
      }));
      setItems(localItems);
      setFilteredItems(localItems);

      try {
        const fetchedCategories = await getAllCategories(true);
        const mainCats: CategoryType[] = fetchedCategories.map(cat => ({
          id: cat.id,
          name: cat.name || '',
          description: cat.description || '',
          parentId: cat.parentId,
          order: cat.order,
          isActive: cat.isActive,
          imageUrl: cat.imageUrl,
          createdAt: typeof cat.createdAt === 'string' ? new Date(cat.createdAt) : cat.createdAt,
          updatedAt: typeof cat.updatedAt === 'string' ? new Date(cat.updatedAt) : cat.updatedAt
        }));
        setMainCategories(mainCats);
        const uniqueCategoryNames = Array.from(new Set(mainCats.map(cat => cat.name)));
        setCategories(uniqueCategoryNames);
      } catch (catErr) {
        console.error('Error loading main categories:', catErr);
        const inventoryCategoryList = await getInventoryCategories();
        setCategories(Array.from(new Set(inventoryCategoryList || [])));
      }

      try {
        const warehouseList = await getAllWarehouses();
        setWarehouses(warehouseList && warehouseList.length > 0 ? warehouseList : [{
          id: 'default',
          name: 'المستودع الافتراضي',
          createdAt: new Date(),
          updatedAt: new Date()
        }]);
      } catch (whErr) {
        setWarehouses([{ id: 'default', name: 'المستودع الافتراضي', createdAt: new Date(), updatedAt: new Date() }]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading inventory data:', err);
      setError('حدث خطأ أثناء تحميل بيانات المخزون');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventoryData();
  }, []);

  useEffect(() => {
    let result = [...items];
    if (selectedCategory !== 'all') {
      result = result.filter(item => (item.category || '').trim().toLowerCase() === selectedCategory.trim().toLowerCase());
    }
    if (selectedWarehouse !== 'all') {
      result = result.filter(item => item.warehouseId === selectedWarehouse);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    }
    setFilteredItems(result);
  }, [items, selectedCategory, searchQuery, selectedWarehouse]);

  const convertToBaseItem = (item: LocalInventoryItem): BaseInventoryItem => ({
    id: item.id || '',
    name: item.name,
    sku: item.sku || `SKU-${Date.now()}`,
    category: item.category,
    categoryId: item.categoryId || '',
    subcategory: item.subcategory || '',
    subcategoryId: item.subcategoryId || '',
    warehouseId: item.warehouseId || 'default',
    warehouseName: item.warehouseName || 'المستودع الافتراضي',
    quantity: item.quantity,
    minQuantity: item.minQuantity,
    purchasePrice: item.purchasePrice || item.costPrice || 0,
    sellingPrice: item.sellingPrice || item.price || 0,
    unitPrice: item.unitPrice || 0,
    supplier: item.supplier || '',
    description: item.description || '',
    imageUrl: item.imageUrl || '',
    image: item.imageUrl || '',
    warehouseQuantities: [{
      warehouseId: item.warehouseId || 'default',
      warehouseName: item.warehouseName || 'المستودع الافتراضي',
      quantity: item.quantity
    }],
    createdAt: item.createdAt || new Date(),
    updatedAt: new Date()
  });

  const convertToExtendedItem = (item: LocalInventoryItem): any => ({
    id: item.id || '',
    name: item.name,
    sku: item.sku || `SKU-${Date.now()}`,
    category: item.category,
    warehouseId: item.warehouseId || 'default',
    warehouseName: item.warehouseName || 'المستودع الافتراضي',
    quantity: item.quantity,
    minQuantity: item.minQuantity,
    purchasePrice: item.costPrice || 0,
    sellingPrice: item.price || 0,
    price: item.price || 0,
    unitPrice: item.unitPrice || 0,
    supplier: item.supplier,
    description: item.description,
    imageUrl: item.imageUrl,
    createdAt: item.createdAt || new Date(),
    updatedAt: item.updatedAt || new Date(),
    isLowStock: item.isLowStock || item.quantity <= (item.minQuantity || 5)
  });

  const handleAddItem = () => {
    window.location.href = '/admin/inventory/add';
  };

  const handleEditItem = (item: LocalInventoryItem) => {
    if (item.id) window.location.href = `/admin/inventory/edit/${item.id}`;
  };

  const handleDeleteConfirm = (item: LocalInventoryItem) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleDeleteItem = async () => {
    if (!currentItem.id) return;
    try {
      await deleteInventoryItem(currentItem.id);
      setSnackbarMessage('تم حذف العنصر بنجاح');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await loadInventoryData();
    } catch (err) {
      setSnackbarMessage('حدث خطأ أثناء حذف العنصر');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handlePostToFacebook = async (item: LocalInventoryItem) => {
    try {
      setSnackbarMessage('جاري النشر على فيسبوك...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      const result = await postProductToFacebook(convertToBaseItem(item));
      if (result.success) {
        setSnackbarMessage('تم النشر على فيسبوك بنجاح!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(`فشل النشر: ${result.message}`);
        setSnackbarSeverity('error');
      }
    } catch (err: any) {
      setSnackbarMessage(`خطأ: ${err.message || 'حدث خطأ غير معروف'}`);
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  const handleFieldChange = (name: string, value: any) => {
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = async (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    setSelectedCategoryId(id);
    if (id !== 'all') {
      const cat = mainCategories.find(c => c.id === id);
      if (cat) {
        setCurrentItem(prev => ({ ...prev, category: cat.name, categoryId: cat.id, subcategory: '', subcategoryId: '' }));
        const subs = await getSubcategories(id);
        setSubcategories(subs.map(s => ({ ...s, createdAt: new Date(), updatedAt: new Date() })) as any);
      }
    } else {
      setCurrentItem(prev => ({ ...prev, category: '', categoryId: '', subcategory: '', subcategoryId: '' }));
      setSubcategories([]);
    }
  };

  const handleSubcategoryChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    setSelectedSubcategoryId(id);
    if (id !== 'all') {
      const sub = subcategories.find(s => s.id === id);
      if (sub) setCurrentItem(prev => ({ ...prev, subcategory: sub.name, subcategoryId: sub.id }));
    } else {
      setCurrentItem(prev => ({ ...prev, subcategory: '', subcategoryId: '' }));
    }
  };

  const handleWarehouseChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    const wh = warehouses.find(w => w.id === id);
    if (wh) setCurrentItem(prev => ({ ...prev, warehouseId: wh.id, warehouseName: wh.name }));
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSnackbarMessage('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      setSelectedImage(file);
      try {
        const base64 = await fileToBase64(file);
        setPreviewImage(base64);
        setCurrentItem(prev => ({ ...prev, imageBase64: base64 }));
      } catch (err) {
        console.error('Error processing image:', err);
      }
    }
  };

  const handleSaveItem = async () => {
    if (!currentItem.name || !currentItem.category) {
      setSnackbarMessage('يرجى ملء الحقول المطلوبة');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    try {
      let finalImageUrl = currentItem.imageUrl;
      if (selectedImage) {
        try {
          finalImageUrl = await uploadToCloudinary(selectedImage, 'inventory');
        } catch (err) {
          console.error('Cloudinary upload failed, using local base64 if available');
          finalImageUrl = currentItem.imageBase64 || '';
        }
      }
      const itemToSave = convertToBaseItem({ ...currentItem, imageUrl: finalImageUrl });
      if (isEditMode && currentItem.id) {
        await updateInventoryItem(currentItem.id, itemToSave);
        setSnackbarMessage('تم تحديث العنصر بنجاح');
      } else {
        await addInventoryItem(itemToSave);
        setSnackbarMessage('تم إضافة العنصر بنجاح');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
      await loadInventoryData();
    } catch (err) {
      setSnackbarMessage('حدث خطأ أثناء حفظ العنصر');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const totalItems = items.length;
  const lowStockItems = items.filter(item => item.quantity <= (item.minQuantity || 5)).length;
  const totalValue = items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);
  const outOfStockItems = items.filter(item => item.quantity === 0).length;

  return (
    <Box sx={{ p: 3 }}>
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>إدارة المخزون</Typography>
              <Typography variant="h6" color="text.secondary">إدارة شاملة لجميع منتجات المخزون والمستودعات</Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadInventoryData} sx={{ borderRadius: 2 }}>تحديث البيانات</Button>
          </Box>
          {error && <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2, mb: 3 }}><Typography>{error}</Typography></Paper>}
        </Box>
      </Fade>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}><StatCard title="إجمالي المنتجات" value={totalItems} icon={<InventoryIcon />} color={theme.palette.primary.main} delay={0} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard title="مخزون منخفض" value={lowStockItems} icon={<WarningIcon />} color={theme.palette.warning.main} delay={100} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard title="قيمة المخزون" value={formatCurrency(totalValue)} icon={<TrendingUpIcon />} color={theme.palette.success.main} delay={200} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard title="نفذت الكمية" value={outOfStockItems} icon={<TrendingDownIcon />} color={theme.palette.error.main} delay={300} /></Grid>
      </Grid>

      <Fade in timeout={800}>
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">البحث والتصفية</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'}><ViewModuleIcon /></IconButton>
              <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'}><ViewListIcon /></IconButton>
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="بحث في المخزون" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="تصفية حسب الفئة" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <MenuItem value="all">جميع الفئات</MenuItem>
                {categories.map((c, i) => <MenuItem key={i} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="تصفية حسب المستودع" value={selectedWarehouse} onChange={(e) => setSelectedWarehouse(e.target.value)}>
                <MenuItem value="all">جميع المستودعات</MenuItem>
                {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" color="primary" startIcon={<Add />} onClick={handleAddItem} sx={{ borderRadius: 2, fontWeight: 'bold' }}>إضافة منتج</Button>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      <Fade in timeout={1000}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>المنتجات ({filteredItems.length})</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={60} /></Box>
          ) : filteredItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <InventoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">لا توجد منتجات مطابقة</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredItems.map((item, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id || idx}>
                  <Grow in timeout={1200 + (idx * 50)}>
                    <Box>
                      <InventoryItemCard
                        item={convertToExtendedItem(item)}
                        onEdit={() => handleEditItem(item)}
                        onDelete={() => handleDeleteConfirm(item)}
                        onFacebookPost={() => handlePostToFacebook(item)}
                      />
                    </Box>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Fade>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'تعديل عنصر' : 'إضافة عنصر جديد'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField fullWidth label="اسم العنصر*" name="name" value={currentItem.name} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="رمز العنصر" name="sku" value={currentItem.sku} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>الفئة *</InputLabel>
                  <Select value={selectedCategoryId} onChange={handleCategoryChange} label="الفئة *">
                    <MenuItem value="all">اختر الفئة</MenuItem>
                    {mainCategories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={subcategories.length === 0}>
                  <InputLabel>الفئة الفرعية</InputLabel>
                  <Select value={selectedSubcategoryId} onChange={handleSubcategoryChange} label="الفئة الفرعية">
                    <MenuItem value="all">اختر الفئة الفرعية</MenuItem>
                    {subcategories.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth label="الوصف" name="description" value={currentItem.description} onChange={handleInputChange} multiline rows={2} /></Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={previewImage || currentItem.imageUrl} variant="rounded" sx={{ width: 80, height: 80 }} />
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>تحميل صورة<input type="file" hidden accept="image/*" onChange={handleImageUpload} /></Button>
                  {(previewImage || currentItem.imageUrl) && <Button color="error" onClick={() => { setSelectedImage(null); setPreviewImage(null); handleFieldChange('imageUrl', ''); }}>حذف</Button>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>المستودع</InputLabel>
                  <Select value={currentItem.warehouseId} onChange={handleWarehouseChange} label="المستودع">
                    {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="الموقع" name="location" value={currentItem.location} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="سعر الشراء" name="costPrice" type="number" value={currentItem.costPrice} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="سعر البيع" name="price" type="number" value={currentItem.price} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="الكمية" name="quantity" type="number" value={currentItem.quantity} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="الحد الأدنى" name="minQuantity" type="number" value={currentItem.minQuantity} onChange={handleInputChange} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDialog(false)}>إلغاء</Button>
        <Button onClick={handleSaveItem} variant="contained" color="primary">حفظ</Button></DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>حذف منتج</DialogTitle>
        <DialogContent><Typography>هل أنت متأكد من حذف المنتج "{currentItem.name}"؟ لا يمكن التراجع عن هذا الإجراء.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setOpenDeleteDialog(false)}>إلغاء</Button><Button onClick={handleDeleteItem} color="error" variant="contained">حذف</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 2, fontWeight: 'bold' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryPage;